import {
  AccessMethod as AccessMethodProto,
  AccessStatus as AccessStatusProto,
  AssessmentReviewStatus as AssessmentReviewStatusProto,
  BookkeepingPendingAction as BookkeepingPendingActionProto,
  PlatformConnection as PlatformConnectionProto,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import { Model } from 'infra/model'
import {
  OtherAccount,
  QuickBooksAccount,
  XeroAccount,
} from 'jobs/bookkeeping/BookkeepingPlatformConnection'
import { once } from 'lodash'
import { computed, makeObservable, observable } from 'mobx'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { timestampToISO } from 'utils/grpc-util'
import { match } from 'utils/pattern-match-util'

export enum BookkeepingAccessMethod {
  Managed = 'Managed',
  Unmanaged = 'Unmanaged',
  Unspecified = 'Unspecified',
}

export enum BookkeepingManagerAction {
  ConfirmAccess = 'ConfirmAccess',
  AssessmentReview = 'AssessmentReview',
  Unspecified = 'Unspecified',
}

export type BookkeepingPendingActionPlatformConnection =
  | QuickBooksAccount
  | XeroAccount
  | OtherAccount

export default class BookkeepingPendingAction extends Model<
  BookkeepingPendingActionProto.AsObject,
  BookkeepingPendingAction
> {
  @observable
  id = Id.none

  @observable
  jobShortId = ''

  @observable
  customer: Member = Member.none

  @observable
  championPro: Member = Member.none

  @observable
  platformConnection?: BookkeepingPendingActionPlatformConnection

  @observable
  jobName = ''

  @observable
  workspaceId = ''

  @observable
  accessMethod = BookkeepingAccessMethod.Unspecified

  @observable
  action = BookkeepingManagerAction.Unspecified

  @observable
  dateCreated = DateZero()

  @observable
  datePendingSince = DateZero()

  constructor(private primeMember: PrimeMemberByPublicId) {
    super()
    makeObservable(this)
  }

  @computed
  get displayAccessMethod() {
    return match(this.accessMethod, {
      [BookkeepingAccessMethod.Managed]: () => 'Firm To Taxfyle to Pro',
      [BookkeepingAccessMethod.Unmanaged]: () => 'Firm to Pro',
      [BookkeepingAccessMethod.Unspecified]: () => 'Unspecified',
    })
  }

  @computed
  get displayAction() {
    return match(this.action, {
      [BookkeepingManagerAction.ConfirmAccess]: () => 'Confirm Access',
      [BookkeepingManagerAction.AssessmentReview]: () => 'Assessment Review',
      [BookkeepingManagerAction.Unspecified]: () => 'Unspecified',
    })
  }

  static get none() {
    return lazyNone()
  }

  parse(
    json: Partial<BookkeepingPendingActionProto.AsObject>,
  ): Partial<BookkeepingPendingAction> {
    return {
      id: json.id,
      jobShortId: json.shortId,
      workspaceId: json.workspaceId,
      customer: json.client
        ? this.primeMember({
            user_public_id: json.client.userPublicId,
            workspace_id: json.workspaceId,
          })
        : undefined,
      championPro: json.championPro?.member
        ? this.primeMember({
            user_public_id: json.championPro.member.userPublicId,
            workspace_id: json.workspaceId,
          })
        : undefined,
      jobName: json.jobName,
      platformConnection: json.platformConnection
        ? toPlatformConnection(json.platformConnection)
        : undefined,
      accessMethod: json.accessMethod
        ? toAccessMethod(json.accessMethod)
        : undefined,
      action:
        json.accessStatus && json.assessmentReviewStatus
          ? mapBookkeepingManagerAction(
              json.accessStatus,
              json.assessmentReviewStatus,
            )
          : undefined,
      dateCreated:
        maybeParseDate(timestampToISO(json.dateCreated)) || DateZero(),
      datePendingSince:
        maybeParseDate(timestampToISO(json.datePendingSince)) || DateZero(),
    }
  }
}

function mapBookkeepingManagerAction(
  accessStatus: AccessStatusProto,
  assessmentReviewStatus: AssessmentReviewStatusProto,
) {
  if (
    accessStatus ===
    AccessStatusProto.MANAGED_ACCESS_ADMIN_CONFIRMATION_REQUESTED
  ) {
    return BookkeepingManagerAction.ConfirmAccess
  }

  if (assessmentReviewStatus === AssessmentReviewStatusProto.REVIEW_REQUESTED) {
    return BookkeepingManagerAction.AssessmentReview
  }

  return BookkeepingManagerAction.Unspecified
}

function toAccessMethod(accessMethod: AccessMethodProto) {
  switch (accessMethod) {
    case AccessMethodProto.MANAGED:
      return BookkeepingAccessMethod.Managed
    case AccessMethodProto.UNMANAGED:
      return BookkeepingAccessMethod.Unmanaged
    default:
      return BookkeepingAccessMethod.Unspecified
  }
}

function toPlatformConnection(
  platformConnection: PlatformConnectionProto.AsObject,
) {
  if (platformConnection.quickBooks) {
    return new QuickBooksAccount().set(platformConnection.quickBooks)
  }

  if (platformConnection.xero) {
    return new XeroAccount().set(platformConnection.xero)
  }

  if (platformConnection.other) {
    return new OtherAccount().set(platformConnection.other)
  }

  return undefined
}

const lazyNone = once(() => new BookkeepingPendingAction(() => Member.none))
